import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Department } from '../models/department.model';
import { AuthenticationService } from './Authentication/authentication.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class DepartmentService {
  private readonly API_URL = `${environment.apiUrl}/departments`;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly toastService = inject(ToastService);

  readonly departments = signal<Department[]>([]);

  constructor() {
    if (this.authService.getAccessToken()) {
      this.loadDepartments().subscribe();
    }
  }

  loadDepartments(): Observable<Department[]> {
    if (!this.authService.getAccessToken()) {
      return of([]);
    }

    return this.http.get<any>(this.API_URL).pipe(
      map((res) => {
        const raw = res.data ? res.data : res;
        return Array.isArray(raw) ? raw : raw?.items || [];
      }),
      tap((depts: Department[]) => this.departments.set(depts)),
      catchError(() => of([])),
    );
  }

  saveDepartment(dto: { name: string; code: string; description?: string; defaultAssigneeId?: string | number; slaPolicyId?: number | null; isAssignTickets?: boolean }): Observable<Department> {
    const payload = {
      name: dto.name,
      code: dto.code.toUpperCase(),
      description: dto.description,
      defaultAssigneeId: dto.defaultAssigneeId ? Number(dto.defaultAssigneeId) : undefined,
      slaPolicyId: dto.slaPolicyId ? Number(dto.slaPolicyId) : null,
      isAssignTickets: dto.isAssignTickets !== false,
    };

    return this.http.post<any>(this.API_URL, payload).pipe(
      map((res) => (res.data ? res.data : res)),
      tap((newDept) => {
        this.loadDepartments().subscribe();
        this.toastService.success('Department Saved', `Department "${newDept.name}" created successfully`);
      }),
    );
  }

  addDepartment(dto: { name: string; code: string; description?: string; defaultAssigneeId?: string | number; slaPolicyId?: number | null; isAssignTickets?: boolean }): Observable<Department> {
    return this.saveDepartment(dto);
  }

  createDepartment(dto: { name: string; code: string; description?: string; defaultAssigneeId?: string | number; slaPolicyId?: number | null; isAssignTickets?: boolean }): Observable<Department> {
    return this.saveDepartment(dto);
  }

  updateDepartment(id: string | number, dto: Partial<Department> & { slaPolicyId?: number | null }): Observable<Department> {
    const payload = {
      ...dto,
      defaultAssigneeId: dto.defaultAssigneeId ? Number(dto.defaultAssigneeId) : undefined,
      slaPolicyId: dto.slaPolicyId !== undefined ? (dto.slaPolicyId ? Number(dto.slaPolicyId) : null) : undefined,
      isAssignTickets: dto.isAssignTickets !== undefined ? dto.isAssignTickets : undefined,
    };

    return this.http.patch<any>(`${this.API_URL}/${id}`, payload).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.loadDepartments().subscribe();
        this.toastService.success('Department Updated', 'Department details updated successfully');
      }),
    );
  }

  deleteDepartment(id: string | number): Observable<void> {
    return this.http.delete<any>(`${this.API_URL}/${id}`).pipe(
      tap(() => {
        this.loadDepartments().subscribe();
        this.toastService.success('Department Deleted', 'Department removed successfully');
      }),
    );
  }
}
